import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Search,
  Plus,
  Trash2,
  Edit2,
  Mail,
  Phone,
  ExternalLink,
  Truck,
  Package,
  Users,
  Upload,
  Download,
  Paperclip,
  Building2,
} from "lucide-react";
import { toast } from "sonner";

const emptyVendor = () => ({
  name: "",
  website: "",
  description: "",
  contacts: [],
  products: [],
  distributor: null,
  attachments: [],
  notes: "",
});

export default function Vendors() {
  const [vendors, setVendors] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // vendor object or null
  const [open, setOpen] = useState(false);

  const load = async () => {
    const r = await api.get("/vendors", { params: q ? { q } : {} });
    setVendors(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onSearch = (e) => {
    if (e.key === "Enter") load();
  };

  const openNew = () => {
    setEditing(emptyVendor());
    setOpen(true);
  };

  const openEdit = (v) => {
    setEditing({
      ...v,
      contacts: v.contacts || [],
      products: v.products || [],
    });
    setOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Archive this vendor?")) return;
    await api.delete(`/vendors/${id}`);
    toast.success("Vendor archived");
    load();
  };

  return (
    <div className="space-y-6" data-testid="vendors-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-700">Supply chain</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Vendors</h1>
          <p className="text-slate-500 text-sm mt-1">
            Product suppliers behind the Xentra Stax — contact details, products and per-user pricing.
          </p>
        </div>
        <Button data-testid="new-vendor-btn" onClick={openNew} className="gap-2 bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4" /> New Vendor
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="vendors-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onSearch}
              placeholder="Search vendors…"
              className="pl-9"
            />
          </div>
          <Button variant="outline" onClick={load}>Search</Button>
        </CardContent>
      </Card>

      {vendors.length === 0 ? (
        <Card className="p-12 text-center border-slate-200">
          <Truck className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <div className="font-heading text-lg text-slate-700">No vendors yet</div>
          <div className="text-sm text-slate-500 mt-1">Add your first product supplier.</div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {vendors.map((v) => (
            <Card
              key={v.id}
              data-testid={`vendor-card-${v.id}`}
              className="border-slate-200 hover:border-blue-200 transition-colors"
            >
              <CardHeader className="border-b border-slate-100 flex flex-row items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-base font-heading">{v.name}</CardTitle>
                  {v.website && (
                    <a
                      href={v.website.startsWith("http") ? v.website : `https://${v.website}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline mt-1"
                    >
                      {v.website} <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  {v.description && (
                    <div className="text-xs text-slate-500 mt-2 leading-relaxed">{v.description}</div>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(v)} data-testid={`edit-vendor-${v.id}`}>
                    <Edit2 className="h-4 w-4 text-slate-400 hover:text-blue-600" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(v.id)}>
                    <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-600" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-5 space-y-4">
                <div className="flex gap-6 text-xs font-mono text-slate-500 uppercase tracking-wider">
                  <span className="flex items-center gap-1.5">
                    <Users className="h-3.5 w-3.5" /> {v.contacts?.length || 0} contacts
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Package className="h-3.5 w-3.5" /> {v.products?.length || 0} products
                  </span>
                </div>

                {v.contacts && v.contacts.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500 mb-1.5">Contacts</div>
                    <div className="space-y-1">
                      {v.contacts.slice(0, 3).map((c, i) => (
                        <div key={i} className="text-xs text-slate-700 flex items-center gap-3 flex-wrap">
                          <span className="font-medium">{c.name}</span>
                          {c.role && <span className="text-slate-500">· {c.role}</span>}
                          {c.email && <span className="inline-flex items-center gap-1 text-slate-500"><Mail className="h-3 w-3" />{c.email}</span>}
                          {c.phone && <span className="inline-flex items-center gap-1 text-slate-500"><Phone className="h-3 w-3" />{c.phone}</span>}
                        </div>
                      ))}
                      {v.contacts.length > 3 && (
                        <div className="text-[11px] font-mono text-slate-400">+{v.contacts.length - 3} more</div>
                      )}
                    </div>
                  </div>
                )}

                {v.products && v.products.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500 mb-1.5">Products</div>
                    <div className="space-y-1.5">
                      {v.products.slice(0, 4).map((p) => (
                        <div key={p.id} className="text-xs flex items-start justify-between gap-3 p-2 bg-slate-50 rounded border border-slate-100">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-slate-900 truncate">{p.name}</div>
                            {p.description && <div className="text-slate-500 truncate">{p.description}</div>}
                          </div>
                          {p.pricing && p.pricing.length > 0 && (
                            <div className="text-right">
                              <div className="font-mono text-blue-700 font-semibold">
                                {fmtMoney(Math.min(...p.pricing.map((x) => x.price_per_user || 0)))}
                                <span className="text-[10px] text-slate-500 font-normal"> /user</span>
                              </div>
                              <div className="text-[10px] font-mono text-slate-500">{p.pricing.length} tier{p.pricing.length === 1 ? "" : "s"}</div>
                            </div>
                          )}
                        </div>
                      ))}
                      {v.products.length > 4 && (
                        <div className="text-[11px] font-mono text-slate-400">+{v.products.length - 4} more</div>
                      )}
                    </div>
                  </div>
                )}

                {(!v.contacts?.length && !v.products?.length) && (
                  <div className="text-xs text-slate-400 italic">No contacts or products yet — click edit to add.</div>
                )}

                {v.distributor?.name && (
                  <div className="pt-2 mt-2 border-t border-slate-100 text-xs text-slate-600">
                    <span className="font-mono text-[10px] uppercase text-slate-400 tracking-wider mr-1">Via</span>
                    <span className="font-medium text-slate-700">{v.distributor.name}</span>
                    {v.distributor.contacts?.length > 0 && (
                      <span className="text-slate-400 ml-1">· {v.distributor.contacts.length} contact{v.distributor.contacts.length === 1 ? "" : "s"}</span>
                    )}
                  </div>
                )}
                {v.attachments?.length > 0 && (
                  <div className="text-xs text-slate-500 flex items-center gap-1 pt-1">
                    <Paperclip className="h-3 w-3" />
                    {v.attachments.length} attachment{v.attachments.length === 1 ? "" : "s"}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {open && editing && (
        <VendorDialog
          open={open}
          setOpen={setOpen}
          vendor={editing}
          onSaved={() => {
            setOpen(false);
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function VendorDialog({ open, setOpen, vendor, onSaved }) {
  const [form, setForm] = useState(vendor);
  const isNew = !vendor.id;

  const updateField = (k, v) => setForm({ ...form, [k]: v });

  // Contacts
  const addContact = () =>
    setForm({ ...form, contacts: [...(form.contacts || []), { name: "", role: "", email: "", phone: "" }] });
  const updateContact = (i, k, v) => {
    const next = [...form.contacts];
    next[i] = { ...next[i], [k]: v };
    setForm({ ...form, contacts: next });
  };
  const removeContact = (i) => {
    const next = [...form.contacts];
    next.splice(i, 1);
    setForm({ ...form, contacts: next });
  };

  // Products
  const addProduct = () =>
    setForm({
      ...form,
      products: [...(form.products || []), { name: "", description: "", pricing: [] }],
    });
  const updateProduct = (i, k, v) => {
    const next = [...form.products];
    next[i] = { ...next[i], [k]: v };
    setForm({ ...form, products: next });
  };
  const removeProduct = (i) => {
    const next = [...form.products];
    next.splice(i, 1);
    setForm({ ...form, products: next });
  };

  // Pricing tiers (per product)
  const addPricing = (pi) => {
    const next = [...form.products];
    next[pi] = {
      ...next[pi],
      pricing: [...(next[pi].pricing || []), { tier: "Standard", price_per_user: 0, currency: "GBP", notes: "" }],
    };
    setForm({ ...form, products: next });
  };
  const updatePricing = (pi, ti, k, v) => {
    const next = [...form.products];
    const tiers = [...(next[pi].pricing || [])];
    tiers[ti] = { ...tiers[ti], [k]: k === "price_per_user" ? Number(v) || 0 : v };
    next[pi] = { ...next[pi], pricing: tiers };
    setForm({ ...form, products: next });
  };
  const removePricing = (pi, ti) => {
    const next = [...form.products];
    const tiers = [...(next[pi].pricing || [])];
    tiers.splice(ti, 1);
    next[pi] = { ...next[pi], pricing: tiers };
    setForm({ ...form, products: next });
  };

  // Distributor (optional) ---------------------------------------------------
  const dist = form.distributor || null;
  const enableDistributor = () =>
    setForm({ ...form, distributor: { name: "", website: "", notes: "", contacts: [] } });
  const removeDistributor = () => setForm({ ...form, distributor: null });
  const updateDistField = (k, v) =>
    setForm({ ...form, distributor: { ...(form.distributor || {}), [k]: v } });
  const addDistContact = () =>
    setForm({
      ...form,
      distributor: {
        ...(form.distributor || {}),
        contacts: [...((form.distributor && form.distributor.contacts) || []), { name: "", role: "", email: "", phone: "" }],
      },
    });
  const updateDistContact = (i, k, v) => {
    const next = [...(form.distributor?.contacts || [])];
    next[i] = { ...next[i], [k]: v };
    setForm({ ...form, distributor: { ...form.distributor, contacts: next } });
  };
  const removeDistContact = (i) => {
    const next = [...(form.distributor?.contacts || [])];
    next.splice(i, 1);
    setForm({ ...form, distributor: { ...form.distributor, contacts: next } });
  };

  // Attachments --------------------------------------------------------------
  const [uploading, setUploading] = useState(false);
  const uploadAttachments = async (fileList) => {
    if (!form.id) {
      toast.error("Save the vendor first before uploading attachments");
      return;
    }
    const fd = new FormData();
    Array.from(fileList).forEach((f) => fd.append("files", f));
    setUploading(true);
    try {
      const r = await api.post(`/vendors/${form.id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const added = r.data?.uploaded || [];
      setForm((f) => ({ ...f, attachments: [...(f.attachments || []), ...added] }));
      toast.success(`${added.length} file${added.length === 1 ? "" : "s"} uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  const deleteAttachment = async (att_id) => {
    if (!window.confirm("Delete this attachment?")) return;
    try {
      await api.delete(`/vendors/${form.id}/attachments/${att_id}`);
      setForm((f) => ({ ...f, attachments: (f.attachments || []).filter((a) => a.id !== att_id) }));
      toast.success("Attachment deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };
  const downloadAttachment = async (att) => {
    try {
      const r = await api.get(`/vendors/${form.id}/attachments/${att.id}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };
  const fmtBytes = (n) => {
    if (!n && n !== 0) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  };

  const save = async () => {
    if (!form.name?.trim()) return toast.error("Vendor name is required");
    // Validate that each product has a name
    for (const p of form.products || []) {
      if (!p.name?.trim()) return toast.error("Every product must have a name");
    }
    const payload = {
      name: form.name,
      website: form.website || null,
      description: form.description || null,
      contacts: form.contacts || [],
      products: form.products || [],
      distributor: form.distributor && form.distributor.name?.trim()
        ? {
            name: form.distributor.name.trim(),
            website: form.distributor.website || null,
            notes: form.distributor.notes || null,
            contacts: form.distributor.contacts || [],
          }
        : null,
      notes: form.notes || null,
    };
    try {
      if (isNew) {
        await api.post("/vendors", payload);
        toast.success("Vendor created");
      } else {
        await api.patch(`/vendors/${form.id}`, payload);
        toast.success("Vendor updated");
      }
      onSaved();
    } catch (e) {
      toast.error("Save failed: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? "New" : "Edit"} vendor</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Basic */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Name *</Label>
              <Input data-testid="vendor-form-name" value={form.name} onChange={(e) => updateField("name", e.target.value)} placeholder="Barracuda Networks" />
            </div>
            <div>
              <Label>Website</Label>
              <Input value={form.website || ""} onChange={(e) => updateField("website", e.target.value)} placeholder="barracuda.com" />
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={2} value={form.description || ""} onChange={(e) => updateField("description", e.target.value)} />
          </div>

          {/* Contacts */}
          <div className="border-t border-slate-200 pt-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-sm font-heading font-semibold text-slate-900">Contacts</div>
                <div className="text-xs text-slate-500">Account managers, technical contacts, support reps.</div>
              </div>
              <Button size="sm" variant="outline" onClick={addContact} data-testid="add-vendor-contact"><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
            </div>
            <div className="space-y-2">
              {(form.contacts || []).map((c, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start p-3 bg-slate-50 rounded-md border border-slate-100">
                  <Input className="col-span-3" placeholder="Name" value={c.name || ""} onChange={(e) => updateContact(i, "name", e.target.value)} />
                  <Input className="col-span-3" placeholder="Role" value={c.role || ""} onChange={(e) => updateContact(i, "role", e.target.value)} />
                  <Input className="col-span-3" placeholder="Email" value={c.email || ""} onChange={(e) => updateContact(i, "email", e.target.value)} />
                  <Input className="col-span-2" placeholder="Phone" value={c.phone || ""} onChange={(e) => updateContact(i, "phone", e.target.value)} />
                  <Button size="icon" variant="ghost" className="col-span-1" onClick={() => removeContact(i)}>
                    <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-600" />
                  </Button>
                </div>
              ))}
              {(!form.contacts || form.contacts.length === 0) && (
                <div className="text-xs text-slate-400 italic px-3 py-2">No contacts. Click Add.</div>
              )}
            </div>
          </div>

          {/* Products + per-tier pricing */}
          <div className="border-t border-slate-200 pt-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-sm font-heading font-semibold text-slate-900">Products & pricing</div>
                <div className="text-xs text-slate-500">Each product can have multiple pricing tiers (e.g. Education, Enterprise).</div>
              </div>
              <Button size="sm" variant="outline" onClick={addProduct} data-testid="add-vendor-product"><Plus className="h-3.5 w-3.5 mr-1" /> Add product</Button>
            </div>

            <div className="space-y-3">
              {(form.products || []).map((p, pi) => (
                <div key={pi} className="rounded-md border border-slate-200 bg-white p-3" data-testid={`vendor-product-${pi}`}>
                  <div className="grid grid-cols-12 gap-2 items-start">
                    <Input className="col-span-4" placeholder="Product name *" value={p.name || ""} onChange={(e) => updateProduct(pi, "name", e.target.value)} />
                    <Input className="col-span-7" placeholder="Description" value={p.description || ""} onChange={(e) => updateProduct(pi, "description", e.target.value)} />
                    <Button size="icon" variant="ghost" className="col-span-1" onClick={() => removeProduct(pi)}>
                      <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-600" />
                    </Button>
                  </div>

                  <div className="mt-3 pl-4 border-l-2 border-blue-100">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500">Pricing tiers (per user)</div>
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => addPricing(pi)} data-testid={`add-pricing-${pi}`}>
                        <Plus className="h-3 w-3 mr-1" /> Tier
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {(p.pricing || []).map((t, ti) => (
                        <div key={ti} className="grid grid-cols-12 gap-2 items-center">
                          <Input className="col-span-3" placeholder="Tier" value={t.tier || ""} onChange={(e) => updatePricing(pi, ti, "tier", e.target.value)} />
                          <Input className="col-span-2" type="number" min="0" step="0.01" placeholder="0.00" value={t.price_per_user ?? 0} onChange={(e) => updatePricing(pi, ti, "price_per_user", e.target.value)} />
                          <Input className="col-span-2" placeholder="GBP" value={t.currency || "GBP"} onChange={(e) => updatePricing(pi, ti, "currency", e.target.value)} />
                          <Input className="col-span-4" placeholder="Notes (e.g. 100+ users)" value={t.notes || ""} onChange={(e) => updatePricing(pi, ti, "notes", e.target.value)} />
                          <Button size="icon" variant="ghost" className="col-span-1" onClick={() => removePricing(pi, ti)}>
                            <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-600" />
                          </Button>
                        </div>
                      ))}
                      {(!p.pricing || p.pricing.length === 0) && (
                        <div className="text-xs text-slate-400 italic">No pricing tiers — click "Tier" to add.</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {(!form.products || form.products.length === 0) && (
                <div className="text-xs text-slate-400 italic px-3 py-2">No products. Click Add product.</div>
              )}
            </div>
          </div>

          {/* Distributor (optional) */}
          <div className="border-t border-slate-200 pt-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-sm font-heading font-semibold text-slate-900 flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-slate-500" /> Distributor
                </div>
                <div className="text-xs text-slate-500">Who do we buy this vendor's products through? (e.g. Exclusive Networks, Infinigate, Westcon.)</div>
              </div>
              {!dist && (
                <Button size="sm" variant="outline" onClick={enableDistributor} data-testid="add-distributor">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add distributor
                </Button>
              )}
              {dist && (
                <Button size="sm" variant="ghost" onClick={removeDistributor} data-testid="remove-distributor">
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                </Button>
              )}
            </div>
            {dist && (
              <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Name *</Label>
                    <Input data-testid="distributor-name" value={dist.name || ""} onChange={(e) => updateDistField("name", e.target.value)} placeholder="Exclusive Networks" />
                  </div>
                  <div>
                    <Label className="text-xs">Website</Label>
                    <Input value={dist.website || ""} onChange={(e) => updateDistField("website", e.target.value)} placeholder="exclusive-networks.com" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Notes</Label>
                  <Textarea rows={2} value={dist.notes || ""} onChange={(e) => updateDistField("notes", e.target.value)} placeholder="Preferred buying channel, deal reg process, portal URL…" />
                </div>

                <div className="pt-2">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500">Distributor contacts</div>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={addDistContact} data-testid="add-distributor-contact">
                      <Plus className="h-3 w-3 mr-1" /> Contact
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {(dist.contacts || []).map((c, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 items-center">
                        <Input className="col-span-3" placeholder="Name" value={c.name || ""} onChange={(e) => updateDistContact(i, "name", e.target.value)} />
                        <Input className="col-span-3" placeholder="Role" value={c.role || ""} onChange={(e) => updateDistContact(i, "role", e.target.value)} />
                        <Input className="col-span-3" placeholder="Email" value={c.email || ""} onChange={(e) => updateDistContact(i, "email", e.target.value)} />
                        <Input className="col-span-2" placeholder="Phone" value={c.phone || ""} onChange={(e) => updateDistContact(i, "phone", e.target.value)} />
                        <Button size="icon" variant="ghost" className="col-span-1" onClick={() => removeDistContact(i)}>
                          <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-600" />
                        </Button>
                      </div>
                    ))}
                    {(!dist.contacts || dist.contacts.length === 0) && (
                      <div className="text-xs text-slate-400 italic">No distributor contacts.</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Attachments */}
          <div className="border-t border-slate-200 pt-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-sm font-heading font-semibold text-slate-900 flex items-center gap-2">
                  <Paperclip className="h-4 w-4 text-slate-500" /> Attachments
                </div>
                <div className="text-xs text-slate-500">Partner agreements, data sheets, deal-reg templates — up to 25 MB per file.</div>
              </div>
              <label className={`inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded border border-slate-200 bg-white cursor-pointer hover:bg-slate-50 ${!form.id ? "opacity-50 cursor-not-allowed" : ""}`}>
                <Upload className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : "Upload"}
                <input
                  type="file"
                  multiple
                  disabled={!form.id || uploading}
                  className="hidden"
                  onChange={(e) => { if (e.target.files?.length) uploadAttachments(e.target.files); e.target.value = ""; }}
                  data-testid="vendor-attachment-input"
                />
              </label>
            </div>
            {!form.id && (
              <div className="text-xs text-slate-400 italic">Save the vendor first, then come back to upload attachments.</div>
            )}
            {form.id && (
              <div className="space-y-1">
                {(form.attachments || []).map((att) => (
                  <div key={att.id} className="flex items-center gap-2 text-sm px-3 py-1.5 rounded border border-slate-100 hover:bg-slate-50" data-testid={`vendor-attachment-${att.id}`}>
                    <Paperclip className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                    <button onClick={() => downloadAttachment(att)} className="truncate text-left text-slate-900 hover:text-blue-600 flex-1" title={att.filename}>
                      {att.filename}
                    </button>
                    <span className="text-xs text-slate-400 flex-shrink-0">{fmtBytes(att.size)}</span>
                    <button onClick={() => downloadAttachment(att)} className="p-1 text-slate-400 hover:text-blue-600" title="Download">
                      <Download className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => deleteAttachment(att.id)} className="p-1 text-slate-400 hover:text-red-600" title="Delete" data-testid={`vendor-attachment-delete-${att.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                {(!form.attachments || form.attachments.length === 0) && (
                  <div className="text-xs text-slate-400 italic">No attachments yet.</div>
                )}
              </div>
            )}
          </div>

          <div>
            <Label>Notes</Label>
            <Textarea rows={2} value={form.notes || ""} onChange={(e) => updateField("notes", e.target.value)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="vendor-form-submit" onClick={save} className="bg-blue-600 hover:bg-blue-700">
            {isNew ? "Create vendor" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
